<template>
    <div class="tw-w-full">
        <label v-if="label && !hideLabel" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">
            {{ label }}
        </label>
        
        <div class="tw-flex tw-gap-2 tw-justify-center" :dir="inputDir">
            <input
                v-for="(digit, index) in digits"
                :key="index"
                :ref="el => setInputRef(el, index)"
                v-model="digits[index]"
                type="text"
                inputmode="numeric"
                pattern="[0-9]"
                maxlength="1"
                class="tw-w-12 tw-h-12 tw-text-center tw-text-lg tw-font-semibold tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-bg-white tw-text-gray-900 tw-transition-colors tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-500 focus:tw-border-primary-500 disabled:tw-bg-gray-100 disabled:tw-text-gray-500"
                :class="{
                    'tw-border-red-500 focus:tw-ring-red-500 focus:tw-border-red-500': error,
                    'tw-bg-gray-100': disabled
                }"
                :disabled="disabled"
                @input="handleInput(index, $event)"
                @keydown="handleKeydown(index, $event)"
                @paste="handlePaste"
            />
        </div>
        
        <p v-if="error" class="tw-text-red-500 tw-text-sm tw-mt-2">
            {{ error }}
        </p>
    </div>
</template>

<script setup>
import { ref, reactive, watch, nextTick, defineProps, defineEmits } from 'vue'

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    error: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    inputDir: {
        type: String,
        default: 'ltr'
    },
    length: {
        type: Number,
        default: 6
    },
    hideLabel: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue'])

const digits = reactive(Array(props.length).fill(''))
const inputRefs = ref([])

const setInputRef = (el, index) => {
    if (el) {
        inputRefs.value[index] = el
    }
}

// Watch for changes in modelValue to update digits
watch(() => props.modelValue, (newValue) => {
    if (newValue.length <= props.length) {
        const chars = newValue.split('')
        for (let i = 0; i < props.length; i++) {
            digits[i] = chars[i] || ''
        }
    }
}, { immediate: true })

// Watch digits for changes to emit value
watch(digits, () => {
    const value = digits.join('')
    emit('update:modelValue', value)
}, { deep: true })

const handleInput = (index, event) => {
    const value = event.target.value.replace(/[^0-9]/g, '')
    
    if (value.length > 0) {
        digits[index] = value[value.length - 1] // Take only the last digit
        
        // Move to next input if available
        if (index < props.length - 1) {
            nextTick(() => {
                inputRefs.value[index + 1]?.focus()
            })
        }
    } else {
        digits[index] = ''
    }
}

const handleKeydown = (index, event) => {
    // Handle backspace
    if (event.key === 'Backspace') {
        if (digits[index] === '' && index > 0) {
            // Move to previous input if current is empty
            nextTick(() => {
                inputRefs.value[index - 1]?.focus()
            })
        } else {
            digits[index] = ''
        }
    }
    
    // Handle arrow keys
    if (event.key === 'ArrowLeft' && index > 0) {
        nextTick(() => {
            inputRefs.value[index - 1]?.focus()
        })
    }
    
    if (event.key === 'ArrowRight' && index < props.length - 1) {
        nextTick(() => {
            inputRefs.value[index + 1]?.focus()
        })
    }
}

const handlePaste = (event) => {
    event.preventDefault()
    const pastedData = event.clipboardData.getData('text').replace(/[^0-9]/g, '')
    
    if (pastedData.length > 0) {
        for (let i = 0; i < Math.min(pastedData.length, props.length); i++) {
            digits[i] = pastedData[i]
        }
        
        // Focus the next empty input or last input
        const nextEmptyIndex = digits.findIndex(digit => digit === '')
        const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : props.length - 1
        
        nextTick(() => {
            inputRefs.value[focusIndex]?.focus()
        })
    }
}
</script>

<style scoped>
/* Additional styles can be added here if needed */
</style> 